import { Button, buttonVariants, cn } from '@today/ui-primitives'
import type { ReactElement } from 'react'
import { type AuthLogo, type AuthShellTone, AuthShellView } from '../auth-shell/auth-shell.view'

/**
 * Props for {@link AuthStatusView}.
 *
 * @property logo - The bound brand's logo.
 * @property tone - The card's status tint.
 * @property title - The screen's heading.
 * @property message - What happened and what to do.
 * @property reason - A reference code (already narrowed by the page) and its label, or nothing.
 * @property action - The link back, rendered as a full-load `<a href>`.
 * @property retry - Optional "Try again" control. When present it is the primary
 *   button and renders before the link, which drops to the secondary style
 *   (TO-640 http-error-pages REQ-003).
 */
export interface AuthStatusViewProps {
	logo?: AuthLogo
	tone: AuthShellTone
	title: string
	message: string
	reason?: { label: string; code: string } | null
	action: { label: string; href: string }
	retry?: { label: string; onRetry: () => void }
}

/**
 * A full-page status card: the sign-in status screens (TO-361 login-surface
 * REQ-006/REQ-007) and the HTTP error-page family (TO-640). Pure — the page
 * narrows the reason and supplies the copy.
 */
export function AuthStatusView({
	logo,
	tone,
	title,
	message,
	reason,
	action,
	retry,
}: AuthStatusViewProps): ReactElement {
	return (
		<AuthShellView logo={logo} title={title} tone={tone}>
			<p className="text-center text-ds-base text-ds-text">{message}</p>
			{reason ? (
				<p data-testid="auth-status-reason" className="text-center text-ds-xs text-ds-text-muted">
					{reason.label} <span className="font-ds-mono">{reason.code}</span>
				</p>
			) : null}
			{retry ? (
				<Button variant="primary" size="md" className="w-full" onPress={retry.onRetry}>
					{retry.label}
				</Button>
			) : null}
			<a
				href={action.href}
				className={cn(buttonVariants({ variant: retry ? 'secondary' : 'primary', size: 'md' }), 'w-full')}
			>
				{action.label}
			</a>
		</AuthShellView>
	)
}
